import React, { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, Save, Upload, Trash2, Minus, Plus, Lock } from "lucide-react";
import { labelTemplateApi } from "@/services/api";
import { apiError } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LoadingState } from "@/components/DataStates";
import { toast } from "sonner";

const RED = "#ED1C24";

// Solid-colour logo silhouette via CSS mask — matches how a single-colour
// thermal head renders the logo (red on M421 red/black paper, else black).
function LogoMark({ src, color, style }) {
  return (
    <div
      data-testid="label-preview-logo"
      style={{
        ...style,
        backgroundColor: color,
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        WebkitMaskSize: "contain",
        maskSize: "contain",
      }}
    />
  );
}

// Live preview of the physical label. Mirrors the mobile Label Designer &
// the Phomemo print engine: logo/brand at top, QR in the middle, door
// reference + optional extra line below, with orientation rotation and
// horizontal/vertical fine-tune offsets applied to the content group.
function LabelPreview({ tpl }) {
  const wMm = Math.max(10, Number(tpl.label_width_mm) || 50);
  const hMm = Math.max(10, Number(tpl.label_height_mm) || 30);
  const isPortrait = (tpl.orientation || "landscape") === "portrait";
  const redBlack = (tpl.color_mode || "mono") === "red_black";
  const brand = tpl.brand_text || tpl.heading || "FD CHECK";

  const pxPerMm = Math.min(6, 300 / Math.max(wMm, hMm));
  const w = wMm * pxPerMm;
  const h = hMm * pxPerMm;
  const contentW = isPortrait ? h : w;
  const contentH = isPortrait ? w : h;

  const xOff = ((Number(tpl.horizontal_offset_dots) || 0) / 8) * pxPerMm;
  const yOff = ((Number(tpl.vertical_offset_dots) || 0) / 8) * pxPerMm;

  const qrSize = Math.max(28, Math.min(contentW * 0.62, contentH * 0.5));

  return (
    <div className="flex flex-col items-center gap-2" data-testid="label-preview">
      <div
        className="relative overflow-hidden rounded bg-white"
        style={{ width: w, height: h, border: "1px solid #E5E7EB" }}
      >
        <div
          className="absolute left-1/2 top-1/2 flex flex-col items-center justify-center gap-1"
          style={{
            width: contentW,
            height: contentH,
            transform: `translate(-50%, -50%) rotate(${isPortrait ? 90 : 0}deg) translate(${xOff}px, ${yOff}px)`,
          }}
        >
          {tpl.label_logo_base64 ? (
            <LogoMark
              src={tpl.label_logo_base64}
              color={redBlack ? RED : "#000"}
              style={{ width: Math.min(contentW * 0.7, 120), height: Math.max(14, contentH * 0.16) }}
            />
          ) : (
            <span
              className="font-bold uppercase leading-none tracking-wide"
              style={{ fontSize: Math.max(7, contentH * 0.09), color: "#000" }}
            >
              {brand}
            </span>
          )}
          <QRCodeSVG value="fdcheck://door/preview" size={qrSize} level="M" includeMargin={false} />
          <span
            className="font-extrabold leading-none text-slate-900"
            style={{ fontSize: Math.max(8, contentH * 0.12) }}
          >
            FD-001
          </span>
          {tpl.extra_line ? (
            <span className="leading-none text-slate-600" style={{ fontSize: Math.max(6, contentH * 0.08) }}>
              {tpl.extra_line}
            </span>
          ) : null}
        </div>
      </div>
      <p className="text-xs text-slate-500">
        {wMm}×{hMm} mm · {isPortrait ? "portrait" : "landscape"} · {redBlack ? "red + black" : "black only"}
      </p>
    </div>
  );
}

function Stepper({ value, step = 8, min, max, onChange, testId }) {
  const clamp = (v) => Math.max(min, Math.min(max, v));
  const v = Number(value) || 0;
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => onChange(clamp(v - step))}>
        <Minus className="h-4 w-4" />
      </Button>
      <span className="min-w-[92px] text-center text-sm font-semibold text-slate-800">
        {v > 0 ? "+" : ""}{v} dots{v !== 0 ? ` (${(v / 8).toFixed(1)} mm)` : ""}
      </span>
      <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => onChange(clamp(v + step))}>
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default function LabelTemplateTab() {
  const { user } = useAuth();
  const isAdmin = !!(user?.is_company_admin || user?.is_platform_admin);
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => {
    labelTemplateApi.get().then((r) => setTpl(r.data || {})).catch(() => setTpl({}));
  }, []);

  const set = (k, v) => setTpl((p) => ({ ...p, [k]: v }));

  const num = useMemo(() => (k, min, max) => (e) => {
    const raw = e.target.value;
    if (raw === "") return set(k, "");
    const n = Math.max(min, Math.min(max, Number(raw)));
    set(k, n);
  }, []);

  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Logo must be under 500 KB"); return; }
    const dataUrl = await new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file);
    });
    set("label_logo_base64", dataUrl);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        brand_text: tpl.brand_text ?? tpl.heading ?? undefined,
        heading: tpl.heading ?? undefined,
        subheading: tpl.subheading ?? undefined,
        footer: tpl.footer ?? undefined,
        contact_line: tpl.contact_line ?? undefined,
        label_width_mm: Number(tpl.label_width_mm) || 50,
        label_height_mm: Number(tpl.label_height_mm) || 30,
        orientation: tpl.orientation || "landscape",
        horizontal_offset_dots: Number(tpl.horizontal_offset_dots) || 0,
        vertical_offset_dots: Number(tpl.vertical_offset_dots) || 0,
        label_logo_base64: tpl.label_logo_base64 || null,
        extra_line: tpl.extra_line || null,
        color_mode: tpl.color_mode || "mono",
      };
      const r = await labelTemplateApi.update(payload);
      setTpl((p) => ({ ...p, ...(r.data || {}) }));
      toast.success("Label template saved");
    } catch (e) { toast.error(apiError(e, "Could not save")); }
    finally { setSaving(false); }
  };

  if (!tpl) return <LoadingState label="Loading label template…" />;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_minmax(280px,360px)]">
      {/* ── Controls ── */}
      <div className="space-y-5">
        {!isAdmin && (
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500" data-testid="label-readonly-note">
            <Lock className="h-4 w-4" /> Read-only — only company admins can change the shared label template.
          </div>
        )}

        <Card className="space-y-4 p-5">
          <h3 className="font-semibold text-slate-800">Content</h3>
          <p className="text-sm text-slate-500">Text shown on the printable QR door labels. The logo (if set) replaces the brand text banner.</p>
          <div className="space-y-1.5">
            <Label>Brand / heading</Label>
            <Input value={tpl.brand_text ?? tpl.heading ?? ""} onChange={(e) => set("brand_text", e.target.value)} data-testid="label-tpl-brand_text" placeholder="e.g. FD CHECK" />
          </div>
          <div className="space-y-1.5">
            <Label>Extra line (optional)</Label>
            <Input value={tpl.extra_line ?? ""} maxLength={30} onChange={(e) => set("extra_line", e.target.value)} data-testid="label-tpl-extra_line" placeholder="e.g. building name or floor" />
          </div>
        </Card>

        <Card className="space-y-4 p-5">
          <h3 className="font-semibold text-slate-800">Label size</h3>
          <p className="text-sm text-slate-500">Set the physical roll size. The M421 handles up to 106 mm wide (4-inch) stock; the M110/M120 up to 50 mm.</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Width (mm)</Label>
              <Input type="number" min={10} max={110} value={tpl.label_width_mm ?? 50} onChange={num("label_width_mm", 10, 110)} data-testid="label-tpl-width" />
            </div>
            <div className="space-y-1.5">
              <Label>Height (mm)</Label>
              <Input type="number" min={10} max={200} value={tpl.label_height_mm ?? 30} onChange={num("label_height_mm", 10, 200)} data-testid="label-tpl-height" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Orientation</Label>
            <Select value={tpl.orientation || "landscape"} onValueChange={(v) => set("orientation", v)}>
              <SelectTrigger data-testid="label-tpl-orientation"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="landscape">Landscape</SelectItem>
                <SelectItem value="portrait">Portrait</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">Portrait rotates the content 90° so the QR + door reference read top-to-bottom on the door edge.</p>
          </div>
        </Card>

        <Card className="space-y-4 p-5">
          <h3 className="font-semibold text-slate-800">Colour mode</h3>
          <p className="text-sm text-slate-500">The Phomemo M421 supports red/black thermal paper. In &ldquo;Red + Black&rdquo; the logo prints in <span className="font-semibold" style={{ color: RED }}>red</span>; everything else stays black.</p>
          <div className="flex gap-2">
            {[["mono", "Black only"], ["red_black", "Red + Black (M421)"]].map(([mode, lbl]) => {
              const active = (tpl.color_mode || "mono") === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  data-testid={`label-tpl-color-${mode}`}
                  onClick={() => set("color_mode", mode)}
                  className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${active ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  {lbl}
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="space-y-4 p-5">
          <h3 className="font-semibold text-slate-800">Logo</h3>
          <div className="flex items-center gap-3">
            {tpl.label_logo_base64 ? (
              <img src={tpl.label_logo_base64} alt="logo" className="h-14 w-auto max-w-[140px] rounded border border-slate-200 object-contain" />
            ) : <span className="text-sm text-slate-400">No logo</span>}
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="label-tpl-logo-upload">
                <Upload className="mr-1.5 h-4 w-4" /> Upload
              </Button>
              {tpl.label_logo_base64 && (
                <Button type="button" variant="ghost" size="sm" className="text-red-600" onClick={() => set("label_logo_base64", null)} data-testid="label-tpl-logo-remove">
                  <Trash2 className="mr-1.5 h-4 w-4" /> Remove
                </Button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={pickLogo} />
          </div>
          <p className="text-xs text-slate-400">Wide logos (3:1) work best. PNG with transparency gives the cleanest red silhouette on the M421.</p>
        </Card>

        <Card className="space-y-4 p-5">
          <h3 className="font-semibold text-slate-800">Fine-tune position</h3>
          <p className="text-sm text-slate-500">Nudge the printed content if it isn&apos;t centred on the media (~1 mm per 8 dots).</p>
          <div className="flex items-center justify-between">
            <Label>Shift L / R</Label>
            <Stepper value={tpl.horizontal_offset_dots} min={-160} max={160} onChange={(v) => set("horizontal_offset_dots", v)} testId="label-tpl-hoffset" />
          </div>
          <div className="flex items-center justify-between">
            <Label>Shift U / D</Label>
            <Stepper value={tpl.vertical_offset_dots} min={-160} max={160} onChange={(v) => set("vertical_offset_dots", v)} testId="label-tpl-voffset" />
          </div>
        </Card>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving || !isAdmin} data-testid="label-tpl-save">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save template
          </Button>
        </div>
      </div>

      {/* ── Live preview ── */}
      <div>
        <Card className="sticky top-4 flex flex-col items-center gap-3 p-6">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Live preview</p>
          <LabelPreview tpl={tpl} />
        </Card>
      </div>
    </div>
  );
}
